import { action, makeAutoObservable, runInAction } from 'mobx';
import { State } from '~@types/store';
import { FieldValues } from '~modules/devices/components/DeviceConfigForm/schema';
import { UserAction } from '~config/notification';
import { t } from '~i18n';
import { Device, DeviceType, WatcherrApiNotFoundError } from '@ixicare/resources';

// TODO: type via @ixicare/resources once the resource is integrated.
export type OtaJob = Record<string, unknown>;

class DeviceStore {
  rootStore: State;
  uuid: string | null = null;
  id: number | null = null;
  imeiNumber: string | null = null;
  deviceType: DeviceType | null = null;
  macAddress: string | null = null;
  phoneNumber: string | null = null;
  firmware: string | null = null;
  configValues: FieldValues = {};
  otaJob: OtaJob | null = null;
  loadingSettings: boolean = false;
  loading: boolean = false;
  saving: boolean = false;
  error: Error | null = null;
  status: string;

  constructor(rootStore: State) {
    makeAutoObservable(this, {
      rootStore: false,
      loadDevice: action.bound,
      loadDeviceSettings: action.bound,
      saveConfig: action.bound,
      getConfigStatus: action.bound,
      hydrate: action.bound,
      hydrateFromSchemaMeta: action.bound,
    });
    this.rootStore = rootStore;
  }

  hydrate(meta: { macAddress: string; deviceType: DeviceType }) {
    this.macAddress = meta.macAddress;
    this.deviceType = meta.deviceType;
  }

  async loadDevice(uuid: string) {
    if (this.loading && this.uuid === uuid) return Promise.resolve();
    this.loading = true;
    this.uuid = uuid;

    return Device.getDevice(uuid).then((res) => {
      runInAction(() => {
        // Drop a response that resolved after we navigated to another device.
        if (this.uuid !== uuid) return;
        this.loading = false;
        this.id = res.id;
        this.macAddress = res.macAddress;
        this.phoneNumber = res.phoneNumber ?? null;
        this.imeiNumber = res.imeiNumber ?? null;
        this.deviceType = res?.deviceType || null;
      });
    });
  }

  async loadDeviceSettings(uuid: string) {
    if (this.loadingSettings && this.uuid === uuid) return Promise.resolve();
    this.loadingSettings = true;
    this.uuid = uuid;
    this.configValues = {};

    return Device.getDeviceSettings(uuid).then((conf: FieldValues) => {
      runInAction(() => {
        if (this.uuid !== uuid) return;
        this.loadingSettings = false;
        this.configValues = conf ?? {};
      });
    });
  }

  async getConfigStatus() {
    if (!this.uuid) return;
    try {
      const job = await Device.getDeviceSettingsOtaJobStatus(this.uuid);
      runInAction(() => {
        this.otaJob = job as unknown as OtaJob;
      });
    } catch (e) {
      if (e instanceof WatcherrApiNotFoundError) {
        runInAction(() => {
          this.otaJob = null;
        });
        return;
      }
      throw e;
    }
  }

  // TODO: Evaluate if schema should return selected device or be generalized.
  hydrateFromSchemaMeta(mac: string | undefined, firmware: string | undefined) {
    if (firmware) this.firmware = firmware;
  }

  async saveConfig(payload: FieldValues): Promise<FieldValues> {
    if (!this.uuid) throw new Error('No device loaded');
    this.saving = true;
    this.error = null;
    // `phone_number` is a Device model attribute, not a config setting — route
    // it to Device.patchDevice. The rest goes to the settings endpoint.
    const { phone_number, ...config } = payload;
    const patchPhone = phone_number !== undefined;
    try {
      let saved: FieldValues = {};
      if (Object.keys(config).length > 0) {
        saved = (await Device.postDeviceSettings(this.uuid, config)).deviceLevelConfiguration;
      }
      let newPhone = this.phoneNumber;
      if (patchPhone && this.id != null) {
        newPhone = (await Device.patchDevice(this.id, { phoneNumber: String(phone_number ?? '') })).phoneNumber ?? null;
      }
      runInAction(() => {
        this.configValues = { ...this.configValues, ...saved };
        this.phoneNumber = newPhone;
        this.saving = false;
        this.rootStore.notifications.addUserActionNotification({
          type: UserAction.DeviceConfigUpdate,
          notificationType: 'success',
          message: t('alerts.device.config.update.success'),
        });
      });
      return saved;
    } catch (e) {
      runInAction(() => {
        this.error = e as Error;
        this.saving = false;
        this.rootStore.notifications.addUserActionNotification({
          type: UserAction.DeviceConfigUpdate,
          notificationType: 'error',
          message: t('alerts.device.config.update.failure'),
        });
      });
      throw e;
    }
  }

  reset() {
    this.uuid = null;
    this.id = null;
    this.deviceType = null;
    this.macAddress = null;
    this.phoneNumber = null;
    this.imeiNumber = null;
    this.firmware = null;
    this.configValues = {};
    this.otaJob = null;
    this.loadingSettings = false;
    this.loading = false;
    this.saving = false;
    this.error = null;
  }
}

export default DeviceStore;
